import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { createDealFolder } from '../../../../api/mutations';
import { getDealFolders } from '../../../../api/queries_v2';
import { FolderTreeSelector } from '../../../../components/folder-tree-selector';
import PropdocsModal from '../../../../components/modal/PropdocsModal';

interface CreateDealFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  parentFolderId?: string;
  onFolderCreated: () => void;
}

export const CreateDealFolderModal: React.FC<CreateDealFolderModalProps> = ({
  isOpen,
  onClose,
  teamId,
  parentFolderId,
  onFolderCreated,
}) => {
  const { formatMessage } = useIntl();
  const [folderName, setFolderName] = useState('');
  const [selectedParentFolderId, setSelectedParentFolderId] = useState<string | null>(parentFolderId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch all deal folders for the tree selector
  const { data: foldersData, loading: foldersLoading } = useQuery(getDealFolders as any, {
    variables: { teamId },
    skip: !isOpen,
  });

  const [createFolder] = useMutation(createDealFolder, {
    onCompleted: () => {
      onFolderCreated();
      handleClose();
    },
    onError: (error) => {
      console.error('Error creating folder:', error);
      setIsSubmitting(false);
      setErrorMessage(error.message || 'Failed to create folder. Please try again.');
    },
  });

  const handleClose = () => {
    setFolderName('');
    setSelectedParentFolderId(parentFolderId || null);
    setIsSubmitting(false);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await createFolder({
        variables: {
          name: folderName.trim(),
          teamId,
          parentFolderId: selectedParentFolderId,
        },
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isSubmitting}
      title={<FormattedMessage id="deals.folders.create" defaultMessage="Create New Folder" />}
    >
      <>
        <div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="folderName" className="form-label">
                <FormattedMessage id="deals.folders.name" defaultMessage="Folder Name" />
              </label>
              <input
                type="text"
                id="folderName"
                className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
                placeholder={formatMessage({
                  id: 'deals.folders.name.placeholder',
                  defaultMessage: 'Enter folder name',
                })}
                required
                autoFocus
              />
              {errorMessage && <div className="invalid-feedback d-block">{errorMessage}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">
                <FormattedMessage id="deals.folders.parentFolder" defaultMessage="Parent Folder" />
              </label>
              {foldersLoading ? (
                <div className="text-muted small">
                  <FormattedMessage id="deals.folders.loading" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={foldersData?.dealFolders?.folders || []}
                  selectedFolderId={selectedParentFolderId}
                  onSelectionChange={setSelectedParentFolderId}
                  disabled={isSubmitting}
                  context="deals"
                />
              )}
            </div>
          </form>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!folderName.trim() || isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="common.creating" defaultMessage="Creating..." />
            ) : (
              <FormattedMessage id="deals.folders.create" defaultMessage="Create Folder" />
            )}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default CreateDealFolderModal;
